import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Col } from "reactstrap";
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "../../../components/GameWonModal";
import { useDarkMode } from "../../../context/DarkModeProvider";
import { useLanguage } from "context/LanguageProvider";

const Searching2D = () => {
    
    const QUESTION_TIME = 10;
    const [currentLevel, setCurrentLevel] = useState(0);
    const [lives, setLives] = useState(3);
    const [wrongAnswers, setWrongAnswers] = useState(0);
    const [clickedCells, setClickedCells] = useState([]);
    const [currentPathIndex, setCurrentPathIndex] = useState(0);
    const [showGameOver, setShowGameOver] = useState(false);
    const [showGameWon, setShowGameWon] = useState(false);
    const [isLevelComplete, setIsLevelComplete] = useState(false);
    const [wrongCell, setWrongCell] = useState(null);
    const [showQuestion, setShowQuestion] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [timeLeft, setTimeLeft] = useState(QUESTION_TIME);
    const [usedQuestions, setUsedQuestions] = useState([]);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [showFeedback, setShowFeedback] = useState(false);
    const { darkMode } = useDarkMode();
    const { selLanguage, toggleLanguage, isLoading } = useLanguage();

    const LEVELS = [
  {
    id: 1,
    name: selLanguage
      ? "Γραμμική Αναζήτηση"
      : "Linear Search",
    description: selLanguage
      ? "Βρες το στοιχείο 34 με γραμμική αναζήτηση."
      : "Find element 34 using linear search.",
    matrix: [
      [12, 7, 23, 18, 45],
      [34, 15, 42, 9, 56],
      [21, 8, 11, 5, 67],
      [94, 52, 33, 19, 77]
    ],
    target: 34,
    searchType: "linear",
    correctPath: [
      [0, 0], [0, 1], [0, 2], [0, 3], [0, 4],
      [1, 0],
    ]
  },
  {
    id: 2,
    name: selLanguage
      ? "Αναζήτηση Staircase"
      : "Staircase Search",
    description: selLanguage
      ? "Βρες το 38 με staircase search. Ξεκίνα από πάνω δεξιά [0,3]. Αν ο στόχος > τρέχον → κάτω, αν < τρέχον → αριστερά."
      : "Find 38 using staircase search. Start from top-right [0,3]. If target > current → go down, if < current → go left.",
    matrix: [
      [10, 20, 30, 40],
      [15, 25, 35, 45],
      [22, 32, 42, 52],
      [28, 38, 48, 58]
    ],
    target: 38,
    searchType: "staircase",
    correctPath: [
      [0, 3],
      [0, 2],
      [1, 2],
      [2, 2],
      [2, 1],
      [3, 1]
    ]
  },
  {
    id: 3,
    name: selLanguage
      ? "Δυαδική Αναζήτηση ανά Γραμμή"
      : "Binary Search per Row",
    description: selLanguage
      ? "Βρες το 53 με δυαδική αναζήτηση σε κάθε ταξινομημένη γραμμή. Έλεγξε το μέσο της γραμμής και μετά αριστερά ή δεξιά."
      : "Find 53 using binary search on each sorted row. Check the middle of the row, then go left or right.",
    matrix: [
      [2, 5, 8, 12, 16, 20, 24],
      [28, 31, 34, 37, 40, 43, 46],
      [47, 50, 53, 56, 59, 62, 65],
      [68, 71, 74, 77, 80, 83, 86],
      [89, 92, 95, 98, 101, 104, 107]
    ],
    target: 53,
    searchType: "binaryPerRow",
    correctPath: [
      [0, 3], [0, 5], [0, 6],
      [1, 3], [1, 5], [1, 6],
      [2, 3], [2, 1], [2, 2]
    ]
  },
  {
    id: 4,
    name: selLanguage
      ? "Δυαδική Αναζήτηση σε Στήλη (Δύσκολο)"
      : "Binary Search in Column (Hard)",
    description: selLanguage
      ? "Βρες το 92 με δυαδική αναζήτηση στην κατάλληλη στήλη. Κάθε στήλη είναι ταξινομημένη από πάνω προς τα κάτω. Πρώτα βρες τη σωστή στήλη, μετά κάνε δυαδική αναζήτηση μέσα σε αυτή."
      : "Find 92 using binary search in the appropriate column. Each column is sorted top to bottom. First find the correct column, then perform binary search inside it.",
    matrix: [
      [31, 44, 61, 84, 107, 134, 156, 178, 203],
      [39, 51, 69, 92, 115, 142, 165, 187, 215],
      [47, 59, 77, 101, 124, 151, 174, 196, 227],
      [54, 67, 85, 109, 133, 159, 183, 205, 239],
      [62, 75, 93, 118, 141, 168, 192, 214, 251],
    ],
    target: 92,
    searchType: "binaryPerColumn",
    correctPath: [
      [2, 0], [3, 0], [4, 0],
      [2, 1], [3, 1], [4, 1],
      [2, 2], [3, 2], [4, 2],
      [2, 3], [1, 3]
    ]
  }
];

    const QUESTIONS = [
    {
        question: selLanguage
            ? "Η γραμμική αναζήτηση διατρέχει όλα τα στοιχεία ενός πίνακα γραμμή προς γραμμή μέχρι να βρει το ζητούμενο."
            : "Linear search scans all elements of an array row by row until it finds the target.",
        answer: true
    },
    {
        question: selLanguage
            ? "Η γραμμική αναζήτηση έχει χρονική πολυπλοκότητα O(log n)."
            : "Linear search has time complexity O(log n).",
        answer: false
    },
    {
        question: selLanguage
            ? "Η γραμμική αναζήτηση μπορεί να χρησιμοποιηθεί σε μη ταξινομημένους πίνακες."
            : "Linear search can be used on unsorted arrays.",
        answer: true
    },
    {
        question: selLanguage
            ? "Η Staircase Search ξεκινά από την πάνω δεξιά γωνία του πίνακα."
            : "Staircase Search starts from the top-right corner of the matrix.",
        answer: true
    },
    {
        question: selLanguage
            ? "Στη Staircase Search, αν το τρέχον στοιχείο είναι μεγαλύτερο από το ζητούμενο, μετακινούμαστε κάτω."
            : "In Staircase Search, if the current value is greater than the target, we move down.",
        answer: false
    },
    {
        question: selLanguage
            ? "Η Staircase Search έχει χρονική πολυπλοκότητα O(m + n)."
            : "Staircase Search has time complexity O(m + n).",
        answer: true
    },
    {
        question: selLanguage
            ? "Η Staircase Search λειτουργεί μόνο σε ταξινομημένους πίνακες."
            : "Staircase Search works only on sorted matrices.",
        answer: true
    },
    {
        question: selLanguage
            ? "Η δυαδική αναζήτηση μπορεί να εφαρμοστεί ανά γραμμή σε 2D πίνακες."
            : "Binary search can be applied per row in 2D matrices.",
        answer: true
    },
    {
        question: selLanguage
            ? "Η δυαδική αναζήτηση ανά γραμμή έχει πολυπλοκότητα O(m × log n)."
            : "Binary search per row has time complexity O(m × log n).",
        answer: true
    },
    {
        question: selLanguage
            ? "Η γραμμική αναζήτηση είναι πάντα πιο γρήγορη από τη δυαδική αναζήτηση."
            : "Linear search is always faster than binary search.",
        answer: false
    },
    {
        question: selLanguage
            ? "Στη Staircase Search, κάθε σύγκριση απορρίπτει ολόκληρη γραμμή ή στήλη."
            : "In Staircase Search, each comparison eliminates an entire row or column.",
        answer: true
    },
    {
        question: selLanguage
            ? "Η δυαδική αναζήτηση απαιτεί ο πίνακας να είναι ταξινομημένος."
            : "Binary search requires the array to be sorted.",
        answer: true
    },
    {
        question: selLanguage
            ? "Η γραμμική αναζήτηση είναι απλή στην υλοποίηση αλλά αργή για μεγάλους πίνακες."
            : "Linear search is simple to implement but slow for large arrays.",
        answer: true
    },
    {
        question: selLanguage
            ? "Η Staircase Search χρησιμοποιεί επιπλέον μνήμη O(n)."
            : "Staircase Search uses extra memory O(n).",
        answer: false
    },
    {
        question: selLanguage
            ? "Η δυαδική αναζήτηση σε flattened πίνακα έχει πολυπλοκότητα O(log(m×n))."
            : "Binary search on a flattened matrix has time complexity O(log(m×n)).",
        answer: true
    }
];

    const level = LEVELS[currentLevel];

    useEffect(() => {
        if (lives <= 0) {
            setShowGameOver(true);
        }
    }, [lives]);

    useEffect(() => {
        let timer;
        if (showQuestion && timeLeft > 0 && !showFeedback) {
            timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
        } else if (timeLeft === 0 && !showFeedback) {
            handleTimeout();
        }
        return () => clearTimeout(timer);
    }, [showQuestion, timeLeft, showFeedback]);

    const getRandomQuestion = () => {
        const availableQuestions = QUESTIONS.filter(
            (_, index) => !usedQuestions.includes(index)
        );

        if (availableQuestions.length === 0) {
            setUsedQuestions([]);
            return QUESTIONS[Math.floor(Math.random() * QUESTIONS.length)];
        }

        const randomIndex = Math.floor(Math.random() * availableQuestions.length);
        const selectedQuestion = availableQuestions[randomIndex];
        const originalIndex = QUESTIONS.indexOf(selectedQuestion);

        setUsedQuestions([...usedQuestions, originalIndex]);
        return selectedQuestion;
    };

    const handleCellClick = (row, col) => {
        if (isLevelComplete || showGameOver || showGameWon || showQuestion) return;

        const expectedCell = level.correctPath[currentPathIndex];
        const cellKey = `${row},${col}`;

        if (clickedCells.includes(cellKey)) return;

        if (expectedCell[0] === row && expectedCell[1] === col) {
            setClickedCells([...clickedCells, cellKey]);

            if (level.matrix[row][col] === level.target) {
                setIsLevelComplete(true);
                setTimeout(() => {
                    if (currentLevel < LEVELS.length - 1) {
                        setCurrentLevel(currentLevel + 1);
                        resetLevelState();
                    } else {
                        setShowGameWon(true);
                    }
                }, 1500);
            } else {
                // Εμφάνιση ερώτησης μόνο στο πρώτο επίπεδο
                if (currentLevel === 0) {
                    const question = getRandomQuestion();
                    setCurrentQuestion(question);
                    setShowQuestion(true);
                    setTimeLeft(QUESTION_TIME);
                    setSelectedAnswer(null);
                    setShowFeedback(false);
                } else {
                    setCurrentPathIndex(currentPathIndex + 1);
                }
            }
        } else {
            setWrongCell(cellKey);
            setLives(lives - 1);
            setTimeout(() => {
                setWrongCell(null);
            }, 800);
        }
    };

    const handleAnswer = (answer) => {
        setSelectedAnswer(answer);
        setShowFeedback(true);

        setTimeout(() => {
            if (answer !== currentQuestion.answer) {
                // Λάθος απάντηση - μετράμε το λάθος
                const newWrongAnswers = wrongAnswers + 1;
                setWrongAnswers(newWrongAnswers);

                // Κάθε 2 λάθος απαντήσεις χάνει μια ζωή
                if (newWrongAnswers % 2 === 0) {
                    setLives(lives - 1);
                }
            }
            // Πάντα προχωράμε στον επόμενο κόμβο, ανεξάρτητα από το αν η απάντηση ήταν σωστή ή λάθος
            setCurrentPathIndex(currentPathIndex + 1);
            setShowQuestion(false);
            setShowFeedback(false);
            setSelectedAnswer(null);
        }, 2500);
    };

    const handleTimeout = () => {
        setSelectedAnswer(null);
        setShowFeedback(true);

        setTimeout(() => {
            const newWrongAnswers = wrongAnswers + 1;
            setWrongAnswers(newWrongAnswers);

            if (newWrongAnswers % 2 === 0) {
                setLives(lives - 1);
            }
            // Πάντα προχωράμε στον επόμενο κόμβο
            setCurrentPathIndex(currentPathIndex + 1);
            setShowQuestion(false);
            setShowFeedback(false);
        }, 2500);
    };

    const resetLevelState = () => {
        setClickedCells([]);
        setCurrentPathIndex(0);
        setIsLevelComplete(false);
        setWrongCell(null);
        setWrongAnswers(0);
        setUsedQuestions([]);
    };

    const resetGame = () => {
        setCurrentLevel(0);
        setLives(3);
        resetLevelState();
        setShowGameOver(false);
        setShowGameWon(false);
    };

    const getCellStatus = (row, col) => {
        const cellKey = `${row},${col}`;
        const isTarget = level.matrix[row][col] === level.target;
        const isClicked = clickedCells.includes(cellKey);
        const isWrong = wrongCell === cellKey;

        if (isWrong) return "wrong";
        if (isClicked && isTarget) return "target-found";
        if (isClicked) return "clicked";
        return "default";
    };

    return (
        <div
            className='main-content introDirectedContainer'
            style={{
                background: darkMode ? "#1E293B" : "var(--customBuildButton)",
            }}
        >
            <GameOverModal
                isVisible={showGameOver}
                onPlayAgain={resetGame}
            />

            <GameWonModal
                isVisible={showGameWon}
                onPlayAgain={resetGame}
            />

            <Col xl="12" lg="12" md="12" sm="12" xs="12">
                <div className="max-w-7xl mx-auto" style={{ padding: '0 10px' }}>
                    <div style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
                        <div style={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "15px",
                            flexWrap: "wrap",
                            gap: "10px"
                        }}>
                            <div style={{
                                color: darkMode ? "white" : "#1E293B",
                                padding: "12px 24px",
                                borderRadius: "12px",
                                fontWeight: "700",
                                fontSize: "clamp(14px, 3vw, 18px)",
                                border: darkMode ? "2px solid rgba(255, 255, 255, 0.2)" : "2px solid #1E293B",
                                display: "flex",
                                alignItems: "center",
                                gap: "10px"
                            }}>
                                {selLanguage?`Επίπεδο ${currentLevel + 1}/${LEVELS.length}`:`Level ${currentLevel + 1}/${LEVELS.length}`}
                            </div>

                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px"
                            }}>
                                <div style={{
                                    display: "flex",
                                    gap: "6px",
                                    alignItems: "center"
                                }}>
                                    {[1, 2, 3].map((heartNum) => (
                                        <div
                                            key={heartNum}
                                            style={{
                                                fontSize: "clamp(20px, 4vw, 24px)",
                                                filter: lives >= heartNum
                                                    ? "drop-shadow(0 4px 8px rgba(220, 38, 38, 0.4))"
                                                    : "grayscale(100%) opacity(0.3)",
                                                transform: lives >= heartNum ? "scale(1)" : "scale(0.8)",
                                                transition: "all 0.3s ease",
                                                animation: lives >= heartNum ? "heartbeat 2s ease-in-out infinite" : "none"
                                            }}
                                        >
                                            ❤️
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <style>{`
              @keyframes heartbeat {
                0%, 100% { transform: scale(1); }
                50% { transform: scale(1.1); }
              }
              @keyframes pulse-green {
                0%, 100% { box-shadow: 0 0 20px rgba(16, 185, 129, 0.6); }
                50% { box-shadow: 0 0 40px rgba(16, 185, 129, 0.9); }
              }
              @keyframes shake {
                0%, 100% { transform: translateX(0); }
                25% { transform: translateX(-5px); }
                75% { transform: translateX(5px); }
              }
              @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              @keyframes slideInBounce {
                0% { opacity: 0; transform: scale(0.5) translateY(-50px); }
                70% { transform: scale(1.05) translateY(0); }
                100% { opacity: 1; transform: scale(1) translateY(0); }
              }
              @keyframes shimmer {
                0% { transform: translateX(-100%); }
                100% { transform: translateX(100%); }
              }
              @keyframes feedbackAppear {
                0% { opacity: 0; transform: scale(0.8) rotate(-5deg); }
                100% { opacity: 1; transform: scale(1) rotate(0deg); }
              }
              @keyframes iconBounce {
                0%, 100% { transform: translateY(0); }
                25% { transform: translateY(-20px); }
                50% { transform: translateY(-10px); }
                75% { transform: translateY(-15px); }
              }
            `}</style>
                    </div>

                    <br />

                    <div className="introDirectedProm" style={{
                        fontSize: "clamp(14px, 2.5vw, 16px)",
                        padding: "10px 15px"
                    }}>
                        {level.description}
                    </div>

                    <div className="introDirectedFrame" style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "clamp(10px, 3vw, 20px)",
                        overflowX: "auto"
                    }}>
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: `auto repeat(${level.matrix[0].length}, minmax(0, 1fr))`,
                            gridTemplateRows: `auto repeat(${level.matrix.length}, minmax(0, 1fr))`,
                            gap: "clamp(4px, 1vw, 8px)",
                            maxWidth: "min(700px, 95vw)",
                            width: "100%"
                        }}>
                            <div></div>

                            {level.matrix[0].map((_, colIndex) => (
                                <div
                                    key={`col-${colIndex}`}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#2432fbff",
                                        fontWeight: "700",
                                        fontSize: "clamp(0.7rem, 2vw, 0.9rem)",
                                        padding: "4px"
                                    }}
                                >
                                    {colIndex}
                                </div>
                            ))}

                            {level.matrix.map((row, rowIndex) => (
                                <React.Fragment key={`row-${rowIndex}`}>
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "#2432fbff",
                                            fontWeight: "700",
                                            fontSize: "clamp(0.7rem, 2vw, 0.9rem)",
                                            padding: "4px"
                                        }}
                                    >
                                        {rowIndex}
                                    </div>

                                    {row.map((value, colIndex) => {
                                        const status = getCellStatus(rowIndex, colIndex);
                                        const numCols = level.matrix[0].length;
                                        const cellSize = numCols >= 7
                                            ? "clamp(35px, 7vw, 60px)"
                                            : "clamp(45px, 9vw, 80px)";
                                        const fontSize = numCols >= 7
                                            ? "clamp(0.7rem, 1.3vw, 1.1rem)"
                                            : "clamp(0.9rem, 1.8vw, 1.3rem)";

                                        return (
                                            <motion.div
                                                key={`${rowIndex},${colIndex}`}
                                                onClick={() => handleCellClick(rowIndex, colIndex)}
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                                animate={
                                                    status === "wrong" ? { x: [-5, 5, -5, 5, 0] } :
                                                        status === "target-found" ? { scale: [1, 1.2, 1] } :
                                                            {}
                                                }
                                                style={{
                                                    width: cellSize,
                                                    height: cellSize,
                                                    background:
                                                        status === "target-found" ? "linear-gradient(135deg, #10b981 0%, #059669 100%)" :
                                                            status === "clicked" ? "linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)" :
                                                                status === "wrong" ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)" :
                                                                    "linear-gradient(135deg, #475569 0%, #334155 100%)",
                                                    borderRadius: numCols >= 7 ? "6px" : "12px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontSize: fontSize,
                                                    fontWeight: "700",
                                                    color: "white",
                                                    cursor: isLevelComplete ? "not-allowed" : "pointer",
                                                    border: status === "target-found" ? "2px solid #34d399" : "1px solid rgba(255, 255, 255, 0.2)",
                                                    boxShadow:
                                                        status === "target-found" ? "0 8px 20px rgba(16, 185, 129, 0.5)" :
                                                            status === "clicked" ? "0 4px 12px rgba(59, 130, 246, 0.4)" :
                                                                "0 3px 8px rgba(0, 0, 0, 0.3)",
                                                    transition: "all 0.3s ease",
                                                    animation: status === "target-found" ? "pulse-green 1.5s ease-in-out infinite" : "none"
                                                }}
                                            >
                                                {value}
                                            </motion.div>
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>

                    {isLevelComplete && (
                        <div style={{
                            textAlign: "center",
                            marginTop: "20px",
                            padding: "clamp(15px, 3vw, 20px)",
                            background: "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)",
                            borderRadius: "16px",
                            color: "#059669",
                            fontWeight: "700",
                            fontSize: "clamp(1rem, 2.5vw, 1.2rem)",
                            boxShadow: "0 10px 25px rgba(16, 185, 129, 0.3)"
                        }}>
                            {selLanguage?"Συγχαρητήρια ολοκλήρωσες με επιτυχία το επίπεδο. Ετοιμάσου για το επόμενο.":"Congratulations on successfully completing the level. Get ready for the next one."}
                        </div>
                    )}
                </div>
            </Col>

            {/* Question Modal */}
            {showQuestion && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.85)',
                    backdropFilter: 'blur(8px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: '20px',
                    animation: 'fadeIn 0.3s ease-out'
                }}>
                    <div style={{
                        background: 'linear-gradient(145deg, #1f2937 0%, #374151 100%)',
                        borderRadius: '24px',
                        padding: 'clamp(20px, 5vw, 40px)',
                        maxWidth: '700px',
                        width: '100%',
                        boxShadow: '0 25px 80px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)',
                        position: 'relative',
                        animation: 'slideInBounce 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
                    }}>
                        {/* Timer Bar */}
                        <div style={{
                            width: '100%',
                            height: '12px',
                            background: '#e2e8f0',
                            borderRadius: '20px',
                            overflow: 'hidden',
                            marginBottom: '20px',
                            boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.1)',
                            border: '2px solid #cbd5e1'
                        }}>
                            <div style={{
                                height: '100%',
                                width: `${(timeLeft / QUESTION_TIME) * 100}%`,
                                background: '#7A8EFF',
                                transition: 'width 1s linear',
                                borderRadius: '20px',
                                boxShadow: '0 0 10px rgba(122, 142, 255, 0.5)',
                                position: 'relative',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                                    animation: 'shimmer 2s infinite'
                                }}></div>
                            </div>
                        </div>

                        {/* Timer Display */}
                        <div style={{
                            textAlign: 'center',
                            fontSize: 'clamp(24px, 5vw, 32px)',
                            fontWeight: '800',
                            color: '#dc2626',
                            marginBottom: '25px',
                            textShadow: '0 2px 8px rgba(220, 38, 38, 0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '10px'
                        }}>
                            <span style={{
                                fontFamily: '"Courier New", monospace',
                                background: 'linear-gradient(135deg, #dc2626, #ef4444)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text'
                            }}>
                                {timeLeft}s
                            </span>
                        </div>

                        {/* Question Text */}
                        <h3 style={{
                            fontSize: 'clamp(16px, 3vw, 20px)',
                            color: '#0f172a',
                            textAlign: 'center',
                            marginBottom: '35px',
                            lineHeight: '1.8',
                            fontWeight: '600',
                            padding: '20px',
                            background: '#eaeaea',
                            borderRadius: '16px',
                            borderLeft: '5px solid #3b82f6',
                            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.1)'
                        }}>
                            {currentQuestion?.question}
                        </h3>

                        {showFeedback ? (
                            <div style={{
                                textAlign: 'center',
                                padding: 'clamp(20px, 4vw, 30px)',
                                borderRadius: '16px',
                                animation: 'feedbackAppear 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
                                border: '3px solid',
                                background: selectedAnswer === currentQuestion?.answer
                                    ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)'
                                    : 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                                borderColor: selectedAnswer === currentQuestion?.answer
                                    ? '#10b981'
                                    : '#ef4444',
                                boxShadow: selectedAnswer === null
                                    ? '0 10px 30px rgba(245, 158, 11, 0.3)'
                                    : selectedAnswer === currentQuestion?.answer
                                        ? '0 10px 30px rgba(16, 185, 129, 0.3)'
                                        : '0 10px 30px rgba(239, 68, 68, 0.3)'
                            }}>

                                <p style={{
                                    fontSize: 'clamp(18px, 3.5vw, 24px)',
                                    fontWeight: '800',
                                    margin: '15px 0',
                                    letterSpacing: '1px',
                                    color: selectedAnswer === currentQuestion?.answer
                                        ? '#047857'
                                        : '#b91c1c'
                                }}>
                                    {selectedAnswer === null ? 'Ο χρόνος τελείωσε!' : selectedAnswer === currentQuestion?.answer ? 'Σωστή απάντηση!' : 'Λάθος απάντηση!'}
                                </p>
                            </div>
                        ) : (
                            <div style={{
                                display: 'flex',
                                flexDirection: window.innerWidth < 500 ? 'column' : 'row',
                                gap: 'clamp(12px, 3vw, 20px)',
                                justifyContent: 'center'
                            }}>
                                <button
                                    onClick={() => handleAnswer(true)}
                                    style={{
                                        flex: 1,
                                        padding: 'clamp(14px, 3vw, 18px) clamp(30px, 6vw, 40px)',
                                        fontSize: 'clamp(16px, 3vw, 20px)',
                                        fontWeight: '700',
                                        border: 'none',
                                        borderRadius: '16px',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
                                        background: 'linear-gradient(135deg, #506ce9ff, #334597ff)',
                                        color: 'white'
                                    }}
                                    onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px) scale(1.03)'}
                                    onMouseLeave={(e) => e.target.style.transform = 'translateY(0) scale(1)'}
                                >
                                    {selLanguage?"Σωστό":"Correct"}
                                </button>
                                <button
                                    onClick={() => handleAnswer(false)}
                                    style={{
                                        flex: 1,
                                        padding: 'clamp(14px, 3vw, 18px) clamp(30px, 6vw, 40px)',
                                        fontSize: 'clamp(16px, 3vw, 20px)',
                                        fontWeight: '700',
                                        border: 'none',
                                        borderRadius: '16px',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
                                        background: 'linear-gradient(135deg, #b81e1eff 0%, #7c0505ff 100%)',
                                        color: 'white'
                                    }}
                                    onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px) scale(1.03)'}
                                    onMouseLeave={(e) => e.target.style.transform = 'translateY(0) scale(1)'}
                                >
                                    {selLanguage?"Λάθος":"Incorrect"}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Searching2D;